"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import { ArrowPathIcon, EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import type { AccountType } from "cmw-client";
import { Button } from "@/components/button";
import { InfoCard } from "@/components/info-card";
import { Input } from "@/components/input";
import { CmwLogo } from "@/components/logo";
import { toast } from "@/components/toast";
import { getApiErrorMessage } from "@/lib/ky/error-message";
import { authPages, type Role } from "@/lib/routes";
import { CODE_LENGTH, CodeInput } from "../../forms/code-input";
import { useRequestPasswordReset } from "../../hooks/use-request-password-reset";
import { useResetPassword } from "../../hooks/use-reset-password";
import { useVerifyPasswordReset } from "../../hooks/use-verify-password-reset";

const PASSWORD_RULE =
  "At least 8 characters, including an uppercase letter, a lowercase letter, a number, and a symbol.";

const accountTypeForRole = (role: Role): AccountType => (role === "practitioner" ? "practioner" : "admin");

export type ForgotPasswordPhase = "request-code" | "verify-code" | "set-password";

type ForgotPasswordFormProps = {
  role: Role;
};

export function ForgotPasswordForm({ role }: ForgotPasswordFormProps) {
  const router = useRouter();
  const requestReset = useRequestPasswordReset();
  const verifyCode = useVerifyPasswordReset();
  const resetPassword = useResetPassword();

  const [phase, setPhase] = useState<ForgotPasswordPhase>("request-code");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { signIn } = authPages(role);
  const accountType = accountTypeForRole(role);

  function sendCode() {
    setError(null);

    requestReset.mutate(
      { email, accountType },
      {
        onSuccess: () => setPhase("verify-code"),
        onError: async (cause) =>
          setError(await getApiErrorMessage(cause, "Could not send the code. Please try again.")),
      },
    );
  }

  function onRequest(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    sendCode();
  }

  function onVerify(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    verifyCode.mutate(
      { email, code, accountType },
      {
        onSuccess: ({ resetToken: token }) => {
          setResetToken(token);
          setPhase("set-password");
        },
        onError: async (cause) => setError(await getApiErrorMessage(cause, "Invalid or expired code")),
      },
    );
  }

  function onSetPassword(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError("Both passwords must match.");
      return;
    }

    resetPassword.mutate(
      { resetToken, newPassword: password },
      {
        onSuccess: () => {
          toast.show("Password updated. Sign in with your new password.");
          router.replace(signIn);
        },
        onError: async (cause) =>
          setError(await getApiErrorMessage(cause, "Could not update your password. Please start again.")),
      },
    );
  }

  const subtitle = {
    "request-code": "Enter your email address and we'll send you a code to set a new password.",
    "verify-code": (
      <>
        {"If an account exists for "}
        <span className="font-semibold leading-[1.4] text-brand-dark-blue">{email}</span>
        {", a reset code is on its way. Enter it below to continue."}
      </>
    ),
    "set-password": "Choose a new password for your account.",
  }[phase];

  return (
    <div className="flex w-full max-w-438 flex-col items-center gap-24 py-64 lg:py-12">
      <div className="flex w-full flex-col items-start gap-8">
        <Link
          href={signIn}
          aria-label="Back to sign in"
          className="flex size-32 items-center justify-center text-brand-dark-blue"
        >
          <ArrowLeftIcon className="size-32" />
        </Link>
      </div>
      <CmwLogo type="vertical" className="hidden lg:block" />

      <div className="flex flex-col items-center gap-8 text-center">
        <h1 className="font-sans text-32 font-semibold leading-[1.3] text-neutral-black">Reset Your Password</h1>
        <p className="text-16 leading-[1.7] text-neutral-black/70">{subtitle}</p>
      </div>

      {phase === "request-code" && (
        <form onSubmit={onRequest} className="flex w-full flex-col gap-24">
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            autoFocus
            label="Email Address"
            placeholder="you@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />

          {error && (
            <div role="alert">
              <InfoCard title="Could not send the code" description={error} />
            </div>
          )}

          <Button type="submit" variant="primary-blue" size="l" disabled={requestReset.isPending} className="w-full">
            {requestReset.isPending ? <ArrowPathIcon className="size-20 animate-spin" /> : "Send Code"}
          </Button>
        </form>
      )}

      {phase === "verify-code" && (
        <>
          <form onSubmit={onVerify} className="flex w-full flex-col items-center gap-24">
            <CodeInput value={code} onChange={setCode} autoFocus />

            {error && (
              <div role="alert" className="w-full">
                <InfoCard title="Verification failed" description={error} />
              </div>
            )}

            <Button
              type="submit"
              variant="primary-blue"
              size="l"
              disabled={code.length < CODE_LENGTH || verifyCode.isPending}
              className="w-full"
            >
              {verifyCode.isPending ? <ArrowPathIcon className="size-20 animate-spin" /> : "Verify Code"}
            </Button>
          </form>

          <p className="text-16 leading-[1.7] text-neutral-black/70">
            {"Didn't receive the code? "}
            <button
              type="button"
              onClick={sendCode}
              disabled={requestReset.isPending}
              className="cursor-pointer font-medium leading-[1.4] text-brand-dark-blue disabled:cursor-not-allowed disabled:opacity-50"
            >
              {requestReset.isPending ? "Sending…" : "Resend"}
            </button>
          </p>
        </>
      )}

      {phase === "set-password" && (
        <form onSubmit={onSetPassword} className="flex w-full flex-col gap-24">
          <div className="flex flex-col gap-16">
            <Input
              id="new-password"
              name="new-password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              required
              minLength={8}
              autoFocus
              label="New Password"
              placeholder="Enter your new password"
              message={PASSWORD_RULE}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              iconRight={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="flex size-24 cursor-pointer items-center justify-center text-neutral-black/70"
                >
                  {showPassword ? <EyeSlashIcon className="size-24" /> : <EyeIcon className="size-24" />}
                </button>
              }
            />

            <Input
              id="confirm-password"
              name="confirm-password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              required
              minLength={8}
              label="Confirm New Password"
              placeholder="Re-enter your new password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
            />
          </div>

          {error && (
            <div role="alert">
              <InfoCard title="Reset failed" description={error} />
            </div>
          )}

          <Button
            type="submit"
            variant="primary-blue"
            size="l"
            disabled={!password || !confirmPassword || resetPassword.isPending}
            className="w-full"
          >
            {resetPassword.isPending ? <ArrowPathIcon className="size-20 animate-spin" /> : "Reset Password"}
          </Button>
        </form>
      )}
    </div>
  );
}
